import type { FacilityIconId } from '../utils/assetFacilityIcons';
import styles from './AssetFacilityIcon.module.css';

type AssetFacilityIconProps = {
  iconId: FacilityIconId;
};

const STROKE = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function AssetFacilityIcon({ iconId }: AssetFacilityIconProps) {
  return (
    <span className={styles.circle} aria-hidden>
      <svg className={styles.svg} viewBox="0 0 24 24" focusable="false">
        {renderIconPaths(iconId)}
      </svg>
    </span>
  );
}

function renderIconPaths(iconId: FacilityIconId) {
  switch (iconId) {
    case 'pool':
      return (
        <>
          <path d="M3 14c2 1 4 1 6 0s4-1 6 0 4 1 6 0" {...STROKE} />
          <path d="M3 18c2 1 4 1 6 0s4-1 6 0 4 1 6 0" {...STROKE} />
        </>
      );
    case 'cycling':
      return (
        <>
          <circle cx="7" cy="17" r="2.5" {...STROKE} />
          <circle cx="17" cy="17" r="2.5" {...STROKE} />
          <path d="M9.5 17h3l2-5h3M12 12l-2-3H7" {...STROKE} />
        </>
      );
    case 'pilates':
      return <path d="M6 14c2-3 4-3 6 0s4 3 6 0M8 10l4-3 4 3" {...STROKE} />;
    case 'shower':
      return (
        <>
          <path d="M8 4v4M12 4v4M16 4v4" {...STROKE} />
          <path d="M6 8h12v2c0 4-2 6-6 6s-6-2-6-6V8z" {...STROKE} />
        </>
      );
    case 'cafe':
      return (
        <>
          <path d="M5 9h10v7a3 3 0 01-3 3H8a3 3 0 01-3-3V9z" {...STROKE} />
          <path d="M15 11h2a2 2 0 010 4h-2" {...STROKE} />
        </>
      );
    case 'snacks':
      return (
        <>
          <path d="M8 10c0-2 1.5-3 4-3s4 1 4 3-1.5 4-4 4-4-1-4-4z" {...STROKE} />
          <path d="M8 14h8M10 17h4" {...STROKE} />
        </>
      );
    case 'locker':
      return (
        <>
          <rect x="5" y="4" width="14" height="16" rx="2" {...STROKE} />
          <path d="M9 12h6" {...STROKE} />
        </>
      );
    case 'childcare':
      return (
        <path d="M12 5a3 3 0 100 6 3 3 0 000-6zM6 19c0-3 2.7-5 6-5s6 2 6 5" {...STROKE} />
      );
    case 'pet':
      return (
        <path d="M8 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM16 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM6 13a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM18 13a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM9.5 18c1.5-2 4-2 5 0 1.5 2.5 4 3 4.5 0" {...STROKE} />
      );
    case 'parking':
      return (
        <>
          <rect x="4" y="4" width="16" height="16" rx="2" {...STROKE} />
          <path d="M10 8h3a2.5 2.5 0 010 5H10V8z" {...STROKE} />
        </>
      );
    case 'wifi':
      return (
        <>
          <path d="M5 12.5a11 11 0 0114 0" {...STROKE} />
          <path d="M8.5 16a6 6 0 017 0" {...STROKE} />
          <circle cx="12" cy="19" r="1" fill="currentColor" />
        </>
      );
    case 'weights':
      return (
        <>
          <path d="M4 10v4M20 10v4" {...STROKE} />
          <rect x="4" y="8" width="3" height="8" rx="1" {...STROKE} />
          <rect x="17" y="8" width="3" height="8" rx="1" {...STROKE} />
          <path d="M7 12h10" {...STROKE} />
        </>
      );
    case 'gym':
      return <path d="M4 14h16M6 10h2v8H6zM16 10h2v8h-2zM10 8v10M14 8v10" {...STROKE} />;
    case 'ac':
      return (
        <>
          <path d="M12 4v16M8 8l4-4 4 4M8 16l4 4 4-4" {...STROKE} />
          <path d="M5 12h14" {...STROKE} />
        </>
      );
    case 'kitchen':
      return (
        <>
          <path d="M6 10h12v8H6zM8 7V5M12 7V4M16 7V5" {...STROKE} />
          <circle cx="12" cy="14" r="2" {...STROKE} />
        </>
      );
    case 'projector':
      return (
        <>
          <path d="M4 14h8l4-3v6l-4-3H4z" {...STROKE} />
          <path d="M16 10l4-2v8l-4-2" {...STROKE} />
        </>
      );
    case 'whiteboard':
      return (
        <>
          <rect x="4" y="5" width="16" height="12" rx="1" {...STROKE} />
          <path d="M7 9h10M7 12h8M7 15h6" {...STROKE} />
        </>
      );
    case 'restroom':
      return (
        <>
          <circle cx="9" cy="8" r="2" {...STROKE} />
          <circle cx="15" cy="8" r="2" {...STROKE} />
          <path d="M9 10v8M15 10v8" {...STROKE} />
        </>
      );
    case 'accessibility':
      return (
        <>
          <circle cx="12" cy="6" r="2" {...STROKE} />
          <path d="M12 8v4M9 20l3-8 3 8M7 12h10" {...STROKE} />
        </>
      );
    case 'security':
      return <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" {...STROKE} />;
    case 'elevator':
      return (
        <>
          <rect x="7" y="4" width="10" height="16" rx="1" {...STROKE} />
          <path d="M12 8v8M9.5 11.5L12 8l2.5 3.5M9.5 16.5L12 20l2.5-3.5" {...STROKE} />
        </>
      );
    case 'printer':
      return (
        <>
          <rect x="6" y="9" width="12" height="8" rx="1" {...STROKE} />
          <path d="M8 9V6h8v3M8 17h8v3H8z" {...STROKE} />
        </>
      );
    case 'scanner':
      return (
        <>
          <rect x="5" y="6" width="14" height="12" rx="1" {...STROKE} />
          <path d="M5 12h14" {...STROKE} />
        </>
      );
    case 'tv':
      return (
        <>
          <rect x="4" y="6" width="16" height="11" rx="1" {...STROKE} />
          <path d="M10 20h4" {...STROKE} />
        </>
      );
    case 'meeting':
      return (
        <>
          <circle cx="8" cy="9" r="1.5" {...STROKE} />
          <circle cx="12" cy="8" r="1.5" {...STROKE} />
          <circle cx="16" cy="9" r="1.5" {...STROKE} />
          <path d="M5 18c2-2 4-3 7-3s5 1 7 3" {...STROKE} />
        </>
      );
    case 'conference':
      return (
        <>
          <rect x="4" y="6" width="10" height="8" rx="1" {...STROKE} />
          <path d="M16 9h3v5h-3M7 14v4" {...STROKE} />
          <circle cx="19" cy="12" r="1" fill="currentColor" />
        </>
      );
    case 'storage':
      return (
        <>
          <path d="M5 7h14v12H5z" {...STROKE} />
          <path d="M9 7V5h6v2M12 11v5" {...STROKE} />
        </>
      );
    case 'microwave':
      return (
        <>
          <rect x="5" y="7" width="14" height="11" rx="1" {...STROKE} />
          <rect x="8" y="10" width="8" height="5" rx="0.5" {...STROKE} />
          <circle cx="17" cy="12" r="0.8" fill="currentColor" />
        </>
      );
    case 'fridge':
      return (
        <>
          <rect x="7" y="4" width="10" height="17" rx="1" {...STROKE} />
          <path d="M7 12h10M10 7v2M10 14v3" {...STROKE} />
        </>
      );
    case 'heating':
      return <path d="M12 20c-3-3-2-7 0-10 2 3 3 7 0 10-2-3-2-7 0-10z" {...STROKE} />;
    case 'desk':
      return (
        <>
          <path d="M4 12h16M6 12v6M18 12v6" {...STROKE} />
          <path d="M10 8h4v4h-4z" {...STROKE} />
        </>
      );
    case 'phone':
      return (
        <>
          <rect x="7" y="4" width="10" height="16" rx="2" {...STROKE} />
          <path d="M10 17h4" {...STROKE} />
        </>
      );
    case 'video':
      return (
        <>
          <rect x="4" y="7" width="11" height="10" rx="1" {...STROKE} />
          <path d="M15 10l5-3v10l-5-3" {...STROKE} />
        </>
      );
    case 'soundproof':
      return (
        <>
          <path d="M6 10v4a3 3 0 003 3h1" {...STROKE} />
          <path d="M10 8v8M14 6c2 1 3 3 3 6s-1 5-3 6" {...STROKE} />
          <path d="M4 4l16 16" {...STROKE} />
        </>
      );
    case 'it':
      return (
        <>
          <rect x="5" y="7" width="14" height="10" rx="1" {...STROKE} />
          <path d="M9 20h6" {...STROKE} />
        </>
      );
    case 'lounge':
      return (
        <>
          <path d="M4 14h16v4H4zM6 14V11a2 2 0 012-2h2" {...STROKE} />
          <path d="M14 9h2a2 2 0 012 2v3" {...STROKE} />
        </>
      );
    case 'access24':
      return (
        <>
          <circle cx="12" cy="12" r="8" {...STROKE} />
          <path d="M12 8v4l3 2" {...STROKE} />
        </>
      );
    case 'sunlight':
      return (
        <>
          <circle cx="12" cy="12" r="3" {...STROKE} />
          <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M6.3 17.7l1.4-1.4M16.3 7.7l1.4-1.4" {...STROKE} />
        </>
      );
    case 'garden':
      return (
        <>
          <path d="M12 20V10" {...STROKE} />
          <path d="M8 12c0-3 2-5 4-5s4 2 4 5" {...STROKE} />
          <path d="M6 20h12" {...STROKE} />
        </>
      );
    case 'water':
      return <path d="M12 4c3 4 5 6.5 5 9a5 5 0 01-10 0c0-2.5 2-5 5-9z" {...STROKE} />;
    case 'maintenance':
      return (
        <>
          <path d="M14 4l6 6-8 8H6v-6l8-8z" {...STROKE} />
          <path d="M13 5l6 6" {...STROKE} />
        </>
      );
    case 'books':
      return (
        <>
          <path d="M6 6h4v14H6a1 1 0 01-1-1V7a1 1 0 011-1zM10 6h4v14h-4V6zM14 6h4a1 1 0 011 1v12a1 1 0 01-1 1h-4V6z" {...STROKE} />
        </>
      );
    case 'shelter':
      return (
        <>
          <path d="M4 11l8-6 8 6" {...STROKE} />
          <path d="M6 11v8h12v-8" {...STROKE} />
          <path d="M10 15h4v4h-4z" {...STROKE} />
        </>
      );
    case 'transit':
      return (
        <>
          <rect x="5" y="8" width="14" height="9" rx="2" {...STROKE} />
          <path d="M7 17h10M8 11h2M14 11h2" {...STROKE} />
        </>
      );
    case 'reception':
      return (
        <>
          <path d="M5 19V8l7-4 7 4v11" {...STROKE} />
          <path d="M9 19v-4h6v4" {...STROKE} />
          <circle cx="12" cy="11" r="1.5" {...STROKE} />
        </>
      );
    case 'ev':
      return (
        <>
          <path d="M13 4l3 3-3 3" {...STROKE} />
          <path d="M7 10h9a3 3 0 013 3v4H4v-4a3 3 0 013-3z" {...STROKE} />
        </>
      );
    case 'bicycle':
      return (
        <>
          <circle cx="7" cy="16" r="2.5" {...STROKE} />
          <circle cx="17" cy="16" r="2.5" {...STROKE} />
          <path d="M9.5 16h3l2-5h3M12 11l-2-3H7" {...STROKE} />
        </>
      );
    case 'privstorage':
      return (
        <>
          <rect x="5" y="5" width="14" height="15" rx="2" {...STROKE} />
          <path d="M9 10h6M9 14h4" {...STROKE} />
          <circle cx="15" cy="14" r="1" fill="currentColor" />
        </>
      );
    case 'default':
    default:
      return (
        <>
          <path d="M4 20V9l8-4 8 4v11" {...STROKE} />
          <path d="M9 20v-6h6v6" {...STROKE} />
        </>
      );
  }
}
